import { useFlag } from '@unleash/proxy-client-react';
import { Collapse, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { MagicWandSvg } from '../../../../assets/icons/MagicWandSvg';
import MinimizeSvg from '../../../../assets/icons/MinimizeSvg';
import theme from '../../../../assets/theme';
import {
  allPromptOutputsSelector,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import useSingleSlideMultiResponsePromptTestCase from '../../../../hooks/useSingleSlideMultiResponsePromptTestCase';
import {
  getAvailablePromptDependencies,
  useVisiblePromptDependentInputValues,
  useVisiblePromptTestCaseInputs,
} from '../../../../hooks/useVisiblePromptTestCaseInputs';
import {
  ById,
  GeneratedStudentResponse,
  LocalPromptDependency,
  PromptInput,
  PromptSingleTestCaseResult,
  PromptSingleTestCaseResultType,
} from '../../../../services/backendService/types';
import { notUndefined } from '../../../../utils/general';
import { zIndexes } from '../../../../utils/zIndexes';
import { Input } from '../../../_atoms/_Input';
import Select from '../../../_atoms/_Select/Select';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { parseSlateStringToText } from '../../../SlideComponents/slateUtil';
import { Slide } from '../../../SlideComponents/slide.types';
import { TabContainer } from '../../DesignerTabs/DesignerTabUtils';
import AiFeedbackPromptBuilder from '../AiFeedbackPromptBuilder';
import { createAiFeedbackPrompt } from '../PromptsTab/newPromptTemplates';
import { findDependencies } from '../PromptsTab/PromptBuilder';
import { GenerateExamples } from './GenerateExamples';
import { getStudentAnswerTypes, studentAnswerBasePrompt } from './studentAnswerUtils';

const initStudentAnswers = (
  slideId: string,
  cachedResults?: PromptSingleTestCaseResult,
) => {
  const studentAnswerTypes = getStudentAnswerTypes(slideId);
  if (!cachedResults) return studentAnswerTypes;
  return studentAnswerTypes.map(studentAnswerType => {
    const res =
      cachedResults[`slide-question-answer.${studentAnswerType.responseId}.${slideId}`];
    return {
      ...studentAnswerType,
      response: res?.value || '',
    };
  });
};
const initStudentAnswersPrompt = (
  slideId: string,
  cachedResults?: PromptSingleTestCaseResult,
) => {
  if (!cachedResults) return JSON.stringify(studentAnswerBasePrompt);
  const res = cachedResults[`example-student-resonse.prompt.${slideId}`];
  return res?.value || JSON.stringify(studentAnswerBasePrompt);
};

const EditAiFeedbackTab = ({
  slide,
  onSlideChange,
  isGenerator,
  promptInputs,
  presentationId,
}: {
  slide: Slide;
  onSlideChange: (slide: Slide) => void;
  promptInputs?: PromptInput[];
  isGenerator?: boolean;
  presentationId: string;
}) => {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');
  const promptDependentInputs = useDesignerUIContext(
    context => context.state.promptDependentInputs,
  );
  const studentAnswerTypes = useMemo(
    () => initStudentAnswers(slide.id, promptDependentInputs),
    [slide.id, promptDependentInputs],
  );
  const studentAnswerPrompt = useMemo(
    () => initStudentAnswersPrompt(slide.id, promptDependentInputs),
    [slide.id, promptDependentInputs],
  );

  const [studentAnswerExamples, setStudentAnswerExamples] = useState<
    GeneratedStudentResponse[]
  >(studentAnswerTypes);
  const [studentAnswerExamplePrompt, setStudentAnswerExamplePrompt] = useState<string>(
    studentAnswerPrompt,
  );

  useEffect(() => {
    if (promptDependentInputs) {
      setStudentAnswerExamples(initStudentAnswers(slide.id, promptDependentInputs));
      setStudentAnswerExamplePrompt(
        initStudentAnswersPrompt(slide.id, promptDependentInputs),
      );
    }
  }, [promptDependentInputs, slide.id]);

  const { generate, isLoading } = useSingleSlideMultiResponsePromptTestCase(
    presentationId,
    slide.id,
  );

  const updatePromptDependentInputs = useCallback(
    (payload: { id: string; value: string; type: PromptSingleTestCaseResultType }) => {
      dispatch({
        type: DesignerUIStateActions.updatePromptDependentInputs,
        payload,
      });
    },
    [dispatch],
  );
  const prompt = useMemo(() => {
    if (slide.prompts?.[0]) return slide.prompts?.[0];
    return createAiFeedbackPrompt();
  }, [slide]);

  const dependencies = useMemo(() => findDependencies(prompt), [prompt]);

  const promptOutputs = useDesignerDataContext(allPromptOutputsSelector);

  const testCaseResults = useMemo(() => {
    if (!prompt.id) return;
    if (!promptDependentInputs) return;

    const answerTypes = getStudentAnswerTypes(slide.id);

    return answerTypes
      .map(answer => {
        const res =
          promptDependentInputs[`${prompt.id}.${answer.responseId}.${slide.id}`];
        return res;
      })
      .filter(notUndefined);
  }, [prompt.id, promptDependentInputs, slide.id]);

  const question = useMemo(() => {
    const aiQuestionElement = slide.elements.find(
      element =>
        element.lockedFromDeletion &&
        (element.type === 'text' || element.type === 'generatorText'),
    );
    if (aiQuestionElement) {
      return parseSlateStringToText(aiQuestionElement.textValue);
    }
  }, [slide]);

  const availablePromptDependencies = useMemo(() => {
    const studentAnswerField: LocalPromptDependency = {
      id: `slide-question-answer`,
      type: 'live',
      label: `Student response`,
      formType: 'shortText',
    };
    const questionField: LocalPromptDependency = {
      id: `slide-question`,
      type: 'live',
      label: `Slide question`,
      formType: 'shortText',
    };
    if (!isGenerator) return [studentAnswerField, questionField];

    const generalAvailablePromptDependencies = getAvailablePromptDependencies({
      promptId: prompt.id,
      promptOutputs,
      promptInputs,
    });

    return [...generalAvailablePromptDependencies, studentAnswerField, questionField];
  }, [isGenerator, promptInputs, promptOutputs, prompt.id]);

  const { visibleInputs } = useVisiblePromptTestCaseInputs({
    availablePromptDependencies,
    promptDependencies: dependencies,
    promptInputs,
  });

  const { visiblePromptDependentInputValues } = useVisiblePromptDependentInputValues({
    visibleInputs,
    promptDependentInputs,
  });

  const testFeedbackDisabled = useMemo(() => {
    //check if there is any input that has not been filled
    const hasEmptyInput = visibleInputs.some(dependency => {
      if (!dependency?.id) return false;
      return !promptDependentInputs?.[dependency.id];
    });

    return !studentAnswerExamples || hasEmptyInput;
  }, [promptDependentInputs, studentAnswerExamples, visibleInputs]);

  const handleGenerateSinglePromptTestCase = useCallback(() => {
    let testCaseInputs: ById<string> = {};

    // add default values form the dependencies themselves
    dependencies.forEach(dependency => {
      const foundInput = promptInputs?.find(
        input => input.id === dependency.dependencyId,
      );

      if (foundInput && foundInput.defaultValue) {
        testCaseInputs = {
          ...testCaseInputs,
          [dependency.dependencyId]: foundInput.defaultValue as string,
        };
      }
    });

    // Overwrite the default values with the prompt dependent inputs
    Object.values(visiblePromptDependentInputValues).forEach(dependency => {
      testCaseInputs = {
        ...testCaseInputs,
        [dependency.id]: dependency.value,
      };
    });
    const inputData = Object.keys(testCaseInputs).filter(key => key.startsWith('input'));
    const outputData = Object.keys(testCaseInputs).filter(key =>
      key.startsWith('prompt'),
    );
    const userInputFormData = inputData.map(key => ({
      id: key,
      type: 'string',
      value: testCaseInputs[key],
    }));

    studentAnswerExamples.forEach(studentAnswer => {
      userInputFormData.push({
        id: `slide-question-answer.${studentAnswer.responseId}`,
        type: 'string',
        value: studentAnswer.response,
      });
    });

    userInputFormData.push({
      id: 'slide-question',
      type: 'string',
      value: parseSlateStringToText(question),
    });

    generate({
      presentationId,
      slideId: slide.id,
      userInputFormData,
      resourceMap: outputData.reduce(
        (acc, key) => ({
          ...acc,
          [key]: {
            id: key,
            type: 'string',
            value: testCaseInputs[key],
          },
        }),
        {},
      ),
    });
  }, [
    dependencies,
    generate,
    presentationId,
    promptInputs,
    question,
    slide.id,
    studentAnswerExamples,
    visiblePromptDependentInputValues,
  ]);

  const promptIdToLabel = (id: string) => {
    const [_, responseId] = id.split('.');
    switch (responseId) {
      case 'i_do_not_know':
        return "I don't know";
      case 'very_poor':
        return 'Poor + poor grammar';
      case 'poor':
        return 'Poor';
      case 'good':
        return 'Good evidence + poor grammar';
      case 'excellent':
        return 'Excellent all round';
      default:
        return id;
    }
  };

  return (
    <CustomTabContainer style={{ paddingTop: 20 }}>
      {isAITemplatesEnabled && (
        <CollapseButtonContainer
          style={{ position: 'absolute', top: 10, right: 10 }}
          onClick={() => {
            dispatch({
              type: DesignerUIStateActions.setSelectedTab,
              payload: {
                tab: 'interactiveElements',
              },
            });
          }}
        >
          <Tooltip title={'Minimize prompt view'}>
            <CollapseButton>
              <IconWrapper>
                <MinimizeSvg />
              </IconWrapper>
            </CollapseButton>
          </Tooltip>
        </CollapseButtonContainer>
      )}
      <Title $fontSize={20}>{`Edit AI Feedback`}</Title>
      <ExpandedPromptEditingContainer>
        {slide && (
          <PromptEditingContainer>
            {slide.type === 'aiAssessedAnswers' && (
              <AiFeedbackPromptBuilder
                onSlideChange={onSlideChange}
                slide={slide}
                isGenerator={isGenerator}
                presentationId={presentationId}
                promptInputs={promptInputs}
                isExpanded={true}
              />
            )}
          </PromptEditingContainer>
        )}

        <StudentResponsesContainer>
          <div>
            <p style={{ margin: '10px 0', flex: 1, fontSize: 16 }}>Test the feedback</p>
            <p style={{ marginBottom: 5, fontSize: 14 }}>Question</p>

            {question && (
              <Tooltip
                title={
                  'This question field is the same as the question field in the slide'
                }
                mouseEnterDelay={1}
              >
                <ActivityQuestion>{question}</ActivityQuestion>
              </Tooltip>
            )}
            <Collapse defaultActiveKey={['1']} ghost style={{ marginTop: '10px' }}>
              <Collapse.Panel
                header="Prompt test case inputs"
                key="1"
                style={{ fontWeight: 'bold' }}
              >
                {visibleInputs.map(dependency => {
                  const dependencyId = dependency?.id;
                  if (!dependencyId) return null;

                  if (dependency.formType === 'dropdown') {
                    return (
                      <Select
                        label={dependency?.label}
                        placeholder={dependency?.defaultValue || 'Select value'}
                        onSelect={val => {
                          updatePromptDependentInputs({
                            id: dependencyId,
                            value: val as string,
                            type:
                              promptDependentInputs?.[dependencyId]?.type ||
                              dependency.formType,
                          });
                        }}
                        value={promptDependentInputs?.[dependencyId]?.value}
                        options={dependency?.constraints?.options || []}
                      ></Select>
                    );
                  }

                  if (dependency.formType === 'longText') {
                    return (
                      <div key={dependencyId}>
                        <TextArea
                          label={dependency?.label}
                          value={promptDependentInputs?.[dependencyId]?.value}
                          placeholder={dependency?.defaultValue || 'Add a value'}
                          style={{ width: '100%' }}
                          maxLength={dependency.constraints?.maxLength}
                          symbolCount={true}
                          onChange={e =>
                            updatePromptDependentInputs({
                              id: dependencyId,
                              value: e.target.value as string,
                              type:
                                promptDependentInputs?.[dependencyId]?.type ||
                                dependency.formType,
                            })
                          }
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={dependencyId}>
                      <Input
                        label={dependency?.label}
                        value={promptDependentInputs?.[dependencyId]?.value}
                        placeholder={dependency?.defaultValue || 'Add a value'}
                        onChange={e =>
                          updatePromptDependentInputs({
                            id: dependencyId,
                            value: e.target.value as string,
                            type:
                              promptDependentInputs?.[dependencyId]?.type ||
                              dependency.formType,
                          })
                        }
                      />
                    </div>
                  );
                })}
              </Collapse.Panel>
            </Collapse>
            <div
              style={{
                display: 'flex',
                marginTop: 10,
                marginBottom: 10,
                alignItems: 'center',
              }}
            >
              <p style={{ marginBottom: 0, flex: 1, fontSize: 14 }}>Student answers</p>
            </div>
            <div>
              <Collapse ghost style={{ marginTop: '10px' }}>
                <Collapse.Panel
                  header="Generate student example answers"
                  key="example-answers"
                  style={{ fontWeight: 'bold' }}
                >
                  <div style={{ paddingBottom: '20px' }}>
                    <GenerateExamples
                      prompt={prompt}
                      promptInputs={promptInputs}
                      slide={slide}
                      onStudentAnswersGenerated={setStudentAnswerExamples}
                      studentAnswerExamplePrompt={studentAnswerExamplePrompt}
                      presentationId={presentationId}
                    />
                  </div>
                </Collapse.Panel>
              </Collapse>
            </div>
            {studentAnswerTypes.map((type, index) => (
              <TextArea
                key={index}
                label={type.label}
                value={
                  studentAnswerExamples
                    ? studentAnswerExamples[index].response
                    : undefined
                }
                rows={6}
                placeholder={`Add a ${type.responsePrompt} student answer`}
                style={{ width: '100%' }}
                maxLength={slide.responseCharacterLimit || undefined}
                symbolCount={true}
                onChange={e => {
                  const findIndex = studentAnswerExamples.findIndex(
                    s => s.responseId === type.responseId,
                  );
                  //update the state without overriting the other values
                  const newStudentAnswerExamples = [...studentAnswerExamples];
                  newStudentAnswerExamples[findIndex] = {
                    ...newStudentAnswerExamples[findIndex],
                    response: e.target.value,
                  };
                  setStudentAnswerExamples(newStudentAnswerExamples);
                }}
              />
            ))}
            <Button
              styles={{ margin: '10px 0' }}
              icon={<MagicWandSvg />}
              onClick={() => handleGenerateSinglePromptTestCase()}
              loading={isLoading}
              disabled={testFeedbackDisabled}
            >
              Test the feedback
            </Button>
          </div>
        </StudentResponsesContainer>

        {testCaseResults && (
          <ResultsContainer>
            <p style={{ margin: '10px 0', flex: 1, fontSize: 16 }}>Results</p>
            {testCaseResults.map((result, index) => (
              <div key={index} style={{ marginTop: 20 }}>
                <TextArea
                  label={promptIdToLabel(result.id)}
                  disabled
                  value={result.value}
                  rows={10}
                  style={{ width: '100%' }}
                  maxLength={slide.responseCharacterLimit || undefined}
                  symbolCount={true}
                />
              </div>
            ))}
          </ResultsContainer>
        )}
      </ExpandedPromptEditingContainer>
    </CustomTabContainer>
  );
};

export default EditAiFeedbackTab;

const CustomTabContainer = styled(TabContainer)`
  padding-top: 20px;
  padding: 20px 0px 0px 0px;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  padding: 0px 20px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

const ExpandedPromptEditingContainer = styled.div`
  height: calc(100% - 38px);
  width: 100%;
  scrollbar-width: none;
  display: grid;
  grid-template-columns: 580px 1fr 1fr;

  @media (max-width: 900px) {
    padding-bottom: 100px;
  }
`;
const PromptEditingContainer = styled.div`
  width: 100%;
  overflow-y: auto;
  scrollbar-width: none;
  padding: 10px 20px 0px 20px;
  @media (max-width: 900px) {
    padding-bottom: 100px;
  }
`;

const ResultsContainer = styled.div`
  gap: 10px;
  overflow-y: auto;
  padding: 10px 20px 0px 20px;
`;
const StudentResponsesContainer = styled.div`
  gap: 10px;
  overflow-y: auto;
  padding: 10px 20px 0px 20px;

  .ant-collapse-content-box,
  .ant-collapse-header {
    padding: 0px !important;
  }
`;

const CollapseButtonContainer = styled.div`
  padding: 0;
  border: none;
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 10px;
  z-index: ${zIndexes('CREATOR_LEFT_BAR_HIDE')};
  svg {
    padding: 0;
  }
`;

const CollapseButton = styled.div`
  background-color: white;
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 10px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  svg {
    padding: 0;
    width: 20px !important;
    height: 20px !important;
  }
`;

const ActivityQuestion = styled.div`
  font-weight: bold;
  font-size: 12px;
  background-color: #e9ecef; /* Light gray background */
  color: #6c757d; /* Dark gray text */

  border: 1px solid #ced4da; /* Gray border */
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  margin-top: 10px;

  /* Optional: Add a "not-allowed" cursor to indicate that the field is disabled */
  cursor: not-allowed;
`;
